import React from "react";

import { validator } from "lib/build/forms/common/invitation";

import { api, getApiErrorData, isApiError } from "src/common/utils/api";
import { FormState, useFormState } from "src/features/forms";
import {
	useCreateError,
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { messages } from "src/views/send-invitation/messages";

export function useForm(): {
	formState: FormState;
	isSubmitted: boolean;
	isLoading: boolean;
	onSubmit(): Promise<void>;
} {
	const { org, mod } = useOrgModParams();

	const formState = useFormState({ validator });
	const [isSubmitted, setSubmitted] = React.useState(false);
	const [isLoading, setLoading] = React.useState(false);

	const formStateRef = React.useRef(formState);
	React.useEffect(() => {
		formStateRef.current = formState;
	}, [formState]);

	const createMessage = useCreateMessage();
	const createError = useCreateError();
	const createGenericError = useCreateGenericError();

	const onSubmit = React.useCallback(async () => {
		setSubmitted(true);
		const { current: formState } = formStateRef;
		if (formState.errors.length === 0) {
			setLoading(true);
			try {
				await api.post(
					`/invites?organization=${org}&module=${mod}`,
					formState.value
				);
				setSubmitted(false);
				setLoading(false);
				formState.setValue({});
				createMessage(messages.sendInvitationSuccess);
			} catch (error) {
				setLoading(false);
				if (isApiError(error)) {
					const { code } = getApiErrorData(error);
					if (code === "invites.sendInvite.emailAlreadyExists") {
						createError(messages.emailAlreadyExists);
					} else {
						createGenericError();
					}
				}
			}
		}
	}, [createMessage, createError, createGenericError, org, mod]);

	return { formState, isSubmitted, isLoading, onSubmit };
}
